import { useEffect } from 'react';
import { canUsePointerFx } from './useMotionPrefs';

// Writes a smoothed pointer position (-1..1) to `--px` / `--py` on the referenced element.
// Layers then translate by `var(--px) * depth`, so each layer can move at its own intensity.
const usePointerParallax = (ref, { ease = 0.08, query } = {}) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || !canUsePointerFx(query)) return undefined;

        const target = { x: 0, y: 0 };
        const current = { x: 0, y: 0 };
        let frame = 0;

        const tick = () => {
            current.x += (target.x - current.x) * ease;
            current.y += (target.y - current.y) * ease;
            el.style.setProperty('--px', current.x.toFixed(4));
            el.style.setProperty('--py', current.y.toFixed(4));
            const settled = Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001;
            frame = settled ? 0 : requestAnimationFrame(tick);
        };
        const kick = () => { if (!frame) frame = requestAnimationFrame(tick); };

        const onMove = (e) => {
            if (e.pointerType !== 'mouse') return;
            const rect = el.getBoundingClientRect();
            target.x = Math.max(-1, Math.min(1, ((e.clientX - rect.left) / rect.width) * 2 - 1));
            target.y = Math.max(-1, Math.min(1, ((e.clientY - rect.top) / rect.height) * 2 - 1));
            kick();
        };
        const onLeave = () => { target.x = 0; target.y = 0; kick(); };

        el.addEventListener('pointermove', onMove, { passive: true });
        el.addEventListener('pointerleave', onLeave);
        return () => {
            cancelAnimationFrame(frame);
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerleave', onLeave);
            el.style.removeProperty('--px');
            el.style.removeProperty('--py');
        };
    }, [ref, ease, query]);
};

export default usePointerParallax;
